import React from "react";
import "../styles/legal.css";

export default function Privacy() {
  return (
    <main className="privacy-page">
      <section className="privacy-hero">
        <h1 className="privacy-title">Privacy Policy</h1>

        <p className="privacy-intro">
          Synergistic Intelligence LLC® ("SI," "we," "us," or "our"), based in
          Nipomo, California, respects your privacy and is committed to
          protecting the personal information you share with us. This Privacy
          Policy explains how we collect, use, disclose, and safeguard your
          information when you visit our website:{" "}
          <a href="https://www.synergisticintelligence.com/">
            https://www.synergisticintelligence.com/
          </a>
          , use the Flow Identity Assessment ("FIA"), book a meeting with our
          team, enroll in the Flow Journey, or otherwise interact with our
          services (collectively, the "Services").
        </p>

        <p className="privacy-intro">
          This Policy covers the SI website only. Once you enroll in the Flow
          Journey and gain access to Circle, our community platform, your
          information there is governed by Circle's own privacy policy, not this
          one. The same applies to any scheduling, video conferencing, or other
          third-party tools we use to deliver our Services (currently including
          Calendly, Google Meet, and Zoom), each governed by that provider's own
          privacy policy.
        </p>

        <p className="privacy-intro">
          By using our Services, you agree to the terms of this Privacy Policy.
          If you do not agree, please do not use our Services.
        </p>

        <p className="privacy-date">
          Effective Date: September 28, 2026
          <br />
          Last Updated: September 28, 2026
        </p>
      </section>

      <section className="privacy-content">
        <PrivacySection title="1. Who We Are">
          <p>
            Synergistic Intelligence LLC is a company registered in Nipomo,
            California, USA. We are the "data controller" (under GDPR) and
            "business" (under CCPA) responsible for your personal information
            collected through our Services.
          </p>

          <p>
            <strong>Contact us:</strong>
          </p>

          <ul>
            <li>
              Alec Lance —{" "}
              <a href="mailto:alec@synergisticintelligence.com">
                alec@synergisticintelligence.com
              </a>
            </li>
            <li>
              Stan Cross —{" "}
              <a href="mailto:stan@synergisticintelligence.com">
                stan@synergisticintelligence.com
              </a>
            </li>
          </ul>
        </PrivacySection>

        <PrivacySection title="2. Information We Collect">
          <p>
            <strong>Information you provide directly:</strong>
          </p>

          <ul>
            <li>First and last name</li>
            <li>Email address</li>
            <li>Assessment responses submitted through the FIA</li>
            <li>
              Billing information, when you enroll in the Flow Journey (see
              Section 3)
            </li>
            <li>Any information you voluntarily share when contacting us</li>
          </ul>

          <p>
            <strong>Information collected automatically:</strong>
          </p>

          <ul>
            <li>
              We do not currently use cookies or analytics tools. If this
              changes, we will update this section and, where required, request
              your consent before deployment.
            </li>
          </ul>

          <p>
            <strong>Information we do not collect:</strong>
          </p>

          <ul>
            <li>
              We do not collect phone numbers, physical addresses, or profile
              photos through the SI website.
            </li>
            <li>
              We do not collect shipping addresses for the Flow Identity
              Journal. Graduates of the Flow Journey receive the journal as an
              Amazon gift purchased by SI; delivery and any address details are
              handled directly between the recipient and Amazon, not by SI.
            </li>
          </ul>

          <p>
            <strong>Meeting bookings:</strong>
          </p>

          <p>
            When you book a meeting through a third-party scheduling tool (such
            as Calendly or Google Meet Scheduling), the information you provide
            is collected and stored directly by that platform, not by SI. Please
            refer to that provider's privacy policy for details, for example,
            Calendly's Privacy Policy.
          </p>
        </PrivacySection>

        <PrivacySection title="3. Payment Information">
          <p>
            When you enroll in the Flow Journey program, you will make a
            one-time payment directly through the Circle Platform. Payment is
            processed via Stripe. Your card details are transmitted directly to
            Stripe and are not stored on SI's own servers or database.
          </p>

          <p>
            We retain limited billing information, such as your name, email, and
            transaction/receipt details, for record-keeping, customer support,
            and tax/accounting purposes.
          </p>

          <p>
            Only after payment is confirmed will you be granted access to
            Circle. Any information you provide on Circle afterward (including a
            profile photo) is governed by Circle's own privacy policy, not this
            one.
          </p>
        </PrivacySection>

        <PrivacySection title="4. How We Use Your Information">
          <p>
            <strong>We use the information we collect to:</strong>
          </p>

          <ul>
            <li>Deliver the FIA and provide your results</li>
            <li>
              Personalize your experience if you enroll in the Flow Journey,
              including comparing your FIA results against your progress to help
              us improve the accuracy of the assessment
            </li>
            <li>Process payment for the Flow Journey</li>
            <li>Respond to inquiries and provide customer support</li>
            <li>
              Send marketing communications, only if you have opted in (see
              Section 8)
            </li>
            <li>
              Improve our courses, content, and the FIA through internal
              analysis of results
            </li>
          </ul>

          <p>
            We do not use your information for automated decision-making that
            produces legal or similarly significant effects on you.
          </p>
        </PrivacySection>

        <PrivacySection title="5. Legal Basis for Processing (GDPR)">
          <p>
            If you are located in the EEA, UK, or Switzerland, we process your
            personal information under the following legal bases:
          </p>

          <table className="privacy-table">
            <thead>
              <tr>
                <th>Purpose</th>
                <th>Legal Basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Providing the FIA and delivering results</td>
                <td>Performance of a contract / pre-contractual request</td>
              </tr>
              <tr>
                <td>
                  Storing and analyzing FIA results to personalize Flow Journey
                  coaching
                </td>
                <td>
                  Legitimate interest (improving service quality and FIA
                  accuracy)
                </td>
              </tr>
              <tr>
                <td>Processing payment for the Flow Journey</td>
                <td>Performance of a contract</td>
              </tr>
              <tr>
                <td>Sending marketing communications</td>
                <td>Consent (opt-in)</td>
              </tr>
              <tr>
                <td>Responding to inquiries</td>
                <td>Legitimate interest / consent</td>
              </tr>
              <tr>
                <td>Booking meetings via Calendly</td>
                <td>Performance of a contract</td>
              </tr>
            </tbody>
          </table>
        </PrivacySection>

        <PrivacySection title="6. Cookies and Tracking Technologies">
          <p>
            We do not currently use cookies, web beacons, or analytics tools. We
            reserve the right to implement these in the future to improve site
            performance. If we do, we will update this Policy and, where legally
            required, obtain your consent via a cookie banner first.
          </p>
        </PrivacySection>

        <PrivacySection title="7. Third-Party Service Providers">
          <table className="privacy-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Purpose</th>
                <th>Data Involved</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Stripe</strong>
                </td>
                <td>Payment processing</td>
                <td>Card and billing details (not stored by SI)</td>
              </tr>
              <tr>
                <td>
                  <strong>Amazon</strong>
                </td>
                <td>
                  Fulfillment of the Flow Identity Journal (gifted to graduates)
                </td>
                <td>
                  Recipient's delivery details, handled directly between Amazon
                  and the recipient
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Calendly</strong>
                </td>
                <td>Meeting scheduling</td>
                <td>Name, email, booking info (stored on Calendly)</td>
              </tr>
              <tr>
                <td>
                  <strong>Google Meet</strong>
                </td>
                <td>Video meetings (planned)</td>
                <td>
                  Similar to Calendly; details TBD as integration is finalized
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Zoom</strong>
                </td>
                <td>Video meetings</td>
                <td>
                  Similar to Calendly; name, email, booking info stored on Zoom
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Circle</strong>
                </td>
                <td>Community/Flow Journey hosting (external)</td>
                <td>Governed by Circle's own policy</td>
              </tr>
            </tbody>
          </table>
        </PrivacySection>

        <PrivacySection title="8. Marketing Communications">
          <p>
            We do not send marketing emails by default. If you choose to click
            ‘Email me my results & resources’ at the end of the FIA, this
            constitutes your affirmative opt-in to receive that content and any
            related follow-up communications. You may unsubscribe at any time
            via the link included in any marketing email, or by contacting us
            directly.
          </p>
        </PrivacySection>

        <PrivacySection title="9. The Flow Identity Assessment (FIA)">
          <p>
            Before beginning the FIA, we collect your first name, last name, and
            email address.
          </p>

          <p>
            <strong>
              Your assessment responses and results are stored in our secure
              database and are:
            </strong>
          </p>

          <ul>
            <li>Accessible only to the SI team (Alec and Stan)</li>
            <li>Used to personalize your Flow Journey experience</li>
            <li>Used internally to evaluate and improve FIA accuracy</li>
            <li>Not shared with any third party</li>
          </ul>

          <p>
            Retention: Currently indefinite. As SI scales, we anticipate
            shifting to 30–90 days, and this Policy will be updated accordingly.
          </p>

          <p>
            Your rights: You may request deletion of your FIA results at any
            time via the contacts in Section 1.
          </p>
        </PrivacySection>

        <PrivacySection title="10. Data Storage and Security">
          <ul>
            <li>
              Personal information, including assessment data, is stored in
              MongoDB.
            </li>
            <li>Data is encrypted in transit and at rest.</li>
            <li>Access is restricted to the SI team (Alec and Stan).</li>
            <li>
              Our infrastructure relies on third-party cloud providers
              (SaaS/IaaS/PaaS); we do not maintain physical servers.
            </li>
            <li>Data is hosted on servers located in the United States.</li>
            <li>
              Payment card data is handled by Stripe and is not stored on SI's
              servers, reducing SI's PCI-DSS scope.
            </li>
          </ul>

          <p>
            No method of transmission or storage is 100% secure, and we cannot
            guarantee absolute security.
          </p>
        </PrivacySection>

        <PrivacySection title="11. International Data Transfers">
          <p>
            If you are located outside the United States, your information will
            be transferred to, stored, and processed in the United States. Where
            required under GDPR, we rely on appropriate safeguards (such as
            Standard Contractual Clauses) for such transfers.
          </p>
        </PrivacySection>

        <PrivacySection title="12. Your Privacy Rights">
          <p>
            <strong>If You Are in the EEA, UK, or Switzerland (GDPR)</strong>
          </p>

          <p>
            You have the right to: access, rectify, erase, restrict or object to
            processing, data portability, withdraw consent, and lodge a
            complaint with your local data protection authority.
          </p>

          <p>We will respond to verified requests within 30 days.</p>

          <p>
            <strong>If You Are a California Resident (CCPA/CPRA)</strong>
          </p>

          <p>
            You have the right to: know what we collect/use/disclose, access a
            copy, delete, correct, and opt out of sale/sharing of personal
            information, SI does not sell or share your personal information
            with third parties for monetary or other valuable consideration.
          </p>

          <p>
            You also have the right to non-discrimination for exercising these
            rights.
          </p>

          <p>We will respond to verified requests within 45 days.</p>

          <p>
            To exercise any of these rights, contact{" "}
            <a href="mailto:alec@synergisticintelligence.com">
              alec@synergisticintelligence.com
            </a>{" "}
            or{" "}
            <a href="mailto:stan@synergisticintelligence.com">
              stan@synergisticintelligence.com
            </a>
            .
          </p>

          <p>
            Note: SI does not currently meet CCPA applicability thresholds but
            extends these rights as a best practice.
          </p>
        </PrivacySection>

        <PrivacySection title="13. Children's Privacy">
          <p>
            Our Services are not intended for individuals under 18. We do not
            knowingly collect personal information from minors, and will delete
            any such information if discovered.
          </p>
        </PrivacySection>

        <PrivacySection title="14. Changes to This Privacy Policy">
          <p>
            We may update this Policy periodically. Changes will be posted here
            with a revised "Last Updated" date.
          </p>
        </PrivacySection>

        <PrivacySection title="15. Contact Us">
          <ul>
            <li>
              Alec Lance —{" "}
              <a href="mailto:alec@synergisticintelligence.com">
                alec@synergisticintelligence.com
              </a>
            </li>
            <li>
              Stan Cross —{" "}
              <a href="mailto:stan@synergisticintelligence.com">
                stan@synergisticintelligence.com
              </a>
            </li>
          </ul>

          <p>
            Synergistic Intelligence® LLC
            <br />
            Nipomo, California, USA
          </p>
        </PrivacySection>
      </section>
    </main>
  );
}

function PrivacySection({ title, children }) {
  return (
    <article className="privacy-section">
      <h2>{title}</h2>

      <div className="privacy-body">{children}</div>
    </article>
  );
}
